<template>
  <div
    class="fcp-section"
    :class="`fcp-section--${section.status}`"
    :data-qa-id="`foundry-contract-popup-section-${section.status}`"
  >
    <div class="fcp-section__layout">
      <div class="fcp-section__content">
        <MdTypography
          v-if="isMixed"
          tag="div"
          variant="heading7--medium"
          color="spirits--900"
          class="fcp-section__foundry-name"
        >
          {{ foundryNamesDisplay }}
        </MdTypography>

        <div v-if="isSingleFoundry && section.status === 'terminating'" class="fcp-section__header">
          <div class="fcp-section__date-line">
            <MdTypography tag="span" variant="heading7--regular" color="spirits--700">
              {{ $t('foundry_contract_popup.scheduled_to_end_prefix') }}
            </MdTypography>
            <MdTypography tag="span" variant="body1--bold" color="spirits--900">
              {{ dateDisplay }}.
            </MdTypography>
          </div>

          <div v-if="section.daysLeft" class="fcp-section__countdown">
            <span class="fcp-section__countdown-badge">
              <MdIconClock size="12px" color="red-babe--700" />
              <MdTypography tag="span" variant="micro1--medium" color="red-babe--700">
                {{ $t('foundry_contract_popup.days_left', { count: section.daysLeft }) }}
              </MdTypography>
            </span>
          </div>
        </div>

        <div class="fcp-section__description">
          <MdTypography tag="p" variant="body2--regular" color="spirits--700">
            {{ mainDescription }}
          </MdTypography>
          <MdTypography
            tag="p"
            variant="body2--regular"
            color="spirits--700"
            class="fcp-section__desc-suffix"
          >
            {{ descriptionSuffix }}
          </MdTypography>
          <MdTypography
            v-if="isAdmin && section.status === 'terminated'"
            tag="p"
            variant="body2--regular"
            color="spirits--700"
            class="fcp-section__desc-cta"
          >
            {{ $t('foundry_contract_popup.desc_terminated_cta_admin') }}
          </MdTypography>
        </div>
      </div>

      <div class="fcp-section__illustration" aria-hidden="true">
        <img :src="illustrationSrc" alt="" />
      </div>
    </div>

    <FoundryContractTable
      v-if="!isSingleFoundry"
      :foundries="section.foundries"
      :showLeavingIn="section.status === 'terminating'"
      :status="section.status"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { MdTypography, MdIconClock } from '@antiqua-dls/vue-components';
import FoundryContractTable from './FoundryContractTable.vue';
import terminatingIllustration from './assets/terminating-illustration.svg';
import terminatedIllustration from './assets/terminated-illustration.svg';

const { t } = useI18n();

const props = defineProps({
  section: {
    type: Object,
    required: true
  },
  isAdmin: {
    type: Boolean,
    required: true
  },
  isSingleFoundry: {
    type: Boolean,
    default: false
  },
  isMixed: {
    type: Boolean,
    default: false
  }
});

const illustrationSrc = computed(() =>
  props.section.status === 'terminating' ? terminatingIllustration : terminatedIllustration
);

const foundryNamesDisplay = computed(() => {
  const foundries = props.section.foundries;
  if (foundries.length <= 2) {
    return foundries.map(f => f.name).join(', ');
  }
  const shown = foundries.slice(0, 2).map(f => f.name).join(', ');
  return `${shown} ${t('foundry_contract_popup.plus_more', { count: foundries.length - 2 })}`;
});

const dateDisplay = computed(() => {
  return props.section.foundries[0]?.terminationDate || '';
});

const mainDescription = computed(() => {
  return props.section.status === 'terminating'
    ? t('foundry_contract_popup.desc_terminating')
    : t('foundry_contract_popup.desc_terminated');
});

const descriptionSuffix = computed(() => {
  if (!props.isAdmin) {
    return t('foundry_contract_popup.desc_contact_admin_nonadmin');
  }
  return props.section.status === 'terminating'
    ? t('foundry_contract_popup.desc_terminating_welcome_admin')
    : t('foundry_contract_popup.desc_terminated_welcome_admin');
});
</script>

<style lang="scss" scoped>
@import './stylesheets/foundry-contract-popup';
.fcp-section {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__layout {
    display: grid;
    grid-template-columns: 1fr 150px;
    gap: 24px;
  }

  &__content {
    min-width: 0;
  }

  &__foundry-name {
    margin: 0 0 8px;
  }

  &__header {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__date-line {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__countdown {
    display: flex;
  }

  &__countdown-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background-color: var(--md-color--red-babe--100);
    padding: 2px 8px;
    border-radius: 4px;
    height: 24px;
    box-sizing: border-box;

    :deep(md-typography) {
      display: inline-flex;
      align-items: center;
      line-height: 1;
    }
  }

  &__description {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__illustration {
    position: relative;

    img {
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: auto;
      max-width: 100%;
    }
  }
}

@include fcp-tablet {
  .fcp-section {
    gap: 16px;

    &__header {
      gap: 4px;
      margin-bottom: 12px;
    }

    &__description {
      gap: 12px;
    }
  }
}

@include fcp-mobile {
  .fcp-section {
    &__layout {
      grid-template-columns: 1fr;
    }

    &__illustration {
      display: none;
    }
  }
}
</style>
